import type { Feature } from '@getstep/proto/feature'
import type {
    FeatureOverride,
    FeatureOverridesStorage,
} from '@getstep/sdk/dist/store/features/FeatureOverridesStorage'
import { observable } from 'mobx'

import {
    makePersistentObservable,
    PersistentStore,
} from './persistence/PersistentStore'
import type { EnvironmentName } from '@app-types/environment'
import { getFeaturesFromUrl, meetsFeatureThreshold } from '@lib/utils/feature'

export class LocalFeatureOverridesStorage
    extends PersistentStore
    implements FeatureOverridesStorage
{
    static readonly className = 'LocalFeatureOverridesStorage'
    static readonly persistentFields = ['_featureOverrides']

    private _featureOverrides: FeatureValues = {}

    private clientSideFeatures: ClientFeatures = {}

    private constructor() {
        super()

        makePersistentObservable<LocalFeatureOverridesStorage>(this, {
            _featureOverrides: observable.deep,
        })
    }

    get featureOverrides(): FeatureValues {
        const { clientSideFeatures } = this
        const env = (process.env.NEXT_PUBLIC_ENV ?? 'dev') as EnvironmentName

        // Collect the values of all the client-side features, based on current environment.
        const clientFeatureValues: FeatureValues = {}
        let feature: keyof typeof Feature
        for (feature in clientSideFeatures) {
            const config = clientSideFeatures[feature]
            if (config && env in config) {
                clientFeatureValues[feature] = config[env]?.value
            }
        }

        // Apply and return the feature overrides on top of the client-side feature values.
        return {
            ...clientFeatureValues,
            ...this._featureOverrides,
        }
    }

    static create(): LocalFeatureOverridesStorage {
        return new LocalFeatureOverridesStorage()
    }

    /**
     * Returns the overridden feature value. If a feature isn't overridden, it defaults to false.
     * @param feature to query whether it is enabled.
     */
    isEnabled(feature: keyof typeof Feature): boolean {
        return this.featureOverrides[feature] ?? false
    }

    isClientSide = (feature: Feature): boolean => {
        return feature in this.clientSideFeatures
    }

    hasOverride(feature: Feature): boolean {
        // Since this class provides the source of truth for client side features, these features
        // are always considered overridden.

        return (
            feature in this.clientSideFeatures ||
            feature in this._featureOverrides
        )
    }

    clearOverrides = (): void => {
        this._featureOverrides = {}
    }

    overrideFeature = (feature: Feature, value: FeatureOverride): void => {
        this._featureOverrides = { ...this._featureOverrides, [feature]: value }
    }

    async load(): Promise<void> {
        const { clientSideFeatures } = this

        if (typeof window !== 'undefined') {
            const overrides = getFeaturesFromUrl(window.location.href)

            if (overrides) {
                Object.entries(overrides).forEach(([feature, value]) => {
                    this.overrideFeature(feature as Feature, value)
                })
            }

            // Iterate over all client-side features, for all environments.
            let feature: keyof typeof Feature
            for (feature in clientSideFeatures) {
                const env = process.env.NEXT_PUBLIC_ENV ?? 'dev'
                const config = (clientSideFeatures[feature] as any)?.[env]

                // Generate a simple anonymous ID since we no longer have Segment
                const anonymousId = crypto.randomUUID()
                // Compute whether feature would be enabled for this environment.
                if (config.value && anonymousId) {
                    config.value = meetsFeatureThreshold(
                        anonymousId + ':' + feature,
                        config.percent,
                    )
                }
            }
        }
    }
}

type FeatureValues = Partial<Record<Feature, boolean>>

type ClientFeatures = { [key in Feature]?: ClientFeatureConfig }
type ClientFeatureConfig = Partial<
    Record<EnvironmentName, { percent: number; value?: boolean }>
>
